import { forwardRef, HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes, ReactNode } from "react";

// Table Container
export type TableProps = HTMLAttributes<HTMLTableElement> & {
  /** Additional CSS classes for container */
  containerClassName?: string;
  /** Test ID for testing */
  "data-testid"?: string;
};

export const TableBase = forwardRef<HTMLTableElement, TableProps>(
  ({ containerClassName, className, "data-testid": testId = "table-base", ...props }, ref) => {
    return (
      <div className={containerClassName} data-testid={`${testId}-container`}>
        <table ref={ref} className={className} data-testid={testId} {...props} />
      </div>
    );
  }
);
TableBase.displayName = "TableBase";

// Table Header
export type TableHeaderProps = HTMLAttributes<HTMLTableSectionElement> & {
  "data-testid"?: string;
};

export const TableHeaderBase = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, "data-testid": testId = "table-header-base", ...props }, ref) => {
    return <thead ref={ref} className={className} data-testid={testId} {...props} />;
  }
);
TableHeaderBase.displayName = "TableHeaderBase";

// Table Body
export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement> & {
  "data-testid"?: string;
};

export const TableBodyBase = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, "data-testid": testId = "table-body-base", ...props }, ref) => {
    return <tbody ref={ref} className={className} data-testid={testId} {...props} />;
  }
);
TableBodyBase.displayName = "TableBodyBase";

// Table Footer
export type TableFooterProps = HTMLAttributes<HTMLTableSectionElement> & {
  "data-testid"?: string;
};

export const TableFooterBase = forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, "data-testid": testId = "table-footer-base", ...props }, ref) => {
    return <tfoot ref={ref} className={className} data-testid={testId} {...props} />;
  }
);
TableFooterBase.displayName = "TableFooterBase";

// Table Row
export type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  "data-testid"?: string;
};

export const TableRowBase = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, "data-testid": testId = "table-row-base", ...props }, ref) => {
    return <tr ref={ref} className={className} data-testid={testId} {...props} />;
  }
);
TableRowBase.displayName = "TableRowBase";

// Table Head Cell
export type TableHeadProps = ThHTMLAttributes<HTMLTableCellElement> & {
  "data-testid"?: string;
};

export const TableHeadBase = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, "data-testid": testId = "table-head-base", ...props }, ref) => {
    return <th ref={ref} className={className} data-testid={testId} {...props} />;
  }
);
TableHeadBase.displayName = "TableHeadBase";

// Table Cell
export type TableCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
  "data-testid"?: string;
};

export const TableCellBase = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, "data-testid": testId = "table-cell-base", ...props }, ref) => {
    return <td ref={ref} className={className} data-testid={testId} {...props} />;
  }
);
TableCellBase.displayName = "TableCellBase";

// Table Pagination
export type TablePaginationProps = HTMLAttributes<HTMLDivElement> & {
  /** Current page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Whether previous page is available */
  canPreviousPage: boolean;
  /** Whether next page is available */
  canNextPage: boolean;
  /** Callback when previous button is clicked */
  onPreviousPage: () => void;
  /** Callback when next button is clicked */
  onNextPage: () => void;
  /** Callback when a specific page is clicked */
  onPageClick?: (page: number) => void;
  /** Render prop for previous button */
  renderPreviousButton?: (props: { onClick: () => void; disabled: boolean }) => ReactNode;
  /** Render prop for next button */
  renderNextButton?: (props: { onClick: () => void; disabled: boolean }) => ReactNode;
  /** Render prop for page button */
  renderPageButton?: (props: { page: number; isActive: boolean; onClick: () => void }) => ReactNode;
  /** Test ID for testing */
  "data-testid"?: string;
};

export const TablePaginationBase = forwardRef<HTMLDivElement, TablePaginationProps>(
  (
    {
      currentPage,
      totalPages,
      canPreviousPage,
      canNextPage,
      onPreviousPage,
      onNextPage,
      onPageClick,
      renderPreviousButton,
      renderNextButton,
      renderPageButton,
      className,
      "data-testid": testId = "table-pagination-base",
      ...props
    },
    ref
  ) => {
    if (totalPages <= 1) return null;

    return (
      <div ref={ref} className={className} data-testid={testId} {...props}>
        {renderPreviousButton ? (
          renderPreviousButton({ onClick: onPreviousPage, disabled: !canPreviousPage })
        ) : (
          <button
            onClick={onPreviousPage}
            disabled={!canPreviousPage}
            data-testid={`${testId}-prev`}
          >
            Previous
          </button>
        )}

        {renderPageButton &&
          [...Array(totalPages).keys()].map((i) => {
            const page = i + 1;
            return renderPageButton({
              page,
              isActive: currentPage === page,
              onClick: () => onPageClick?.(page),
            });
          })}

        {renderNextButton ? (
          renderNextButton({ onClick: onNextPage, disabled: !canNextPage })
        ) : (
          <button onClick={onNextPage} disabled={!canNextPage} data-testid={`${testId}-next`}>
            Next
          </button>
        )}
      </div>
    );
  }
);
TablePaginationBase.displayName = "TablePaginationBase";

export default TableBase;

